import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default marker icon issue in Leaflet
import L from "leaflet";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [28.6139, 77.209];

const reverseGeocode = async (lat, lng, timeoutMs = 8000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const params = new URLSearchParams({
    format: "jsonv2",
    addressdetails: "1",
    lat: String(lat),
    lon: String(lng),
  });

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?${params.toString()}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      throw new Error(`Reverse geocoding failed with status ${response.status}`);
    }

    return response.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

const forwardGeocode = async (query, timeoutMs = 8000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const params = new URLSearchParams({
    format: "jsonv2",
    addressdetails: "1",
    q: query,
    limit: "1",
  });

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      },
    );
    if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);
    return response.json();
  } finally {
    clearTimeout(timeoutId);
  }
};


const parseLocationCoordinates = (locationValue) => {
  if (typeof locationValue !== "string") return null;

  const [latRaw, lngRaw] = locationValue.split(",").map((value) => value.trim());
  const lat = Number(latRaw);
  const lng = Number(lngRaw);

  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return { lat, lng };
};

function MapViewport({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], map.getZoom());
    }

    map.invalidateSize();
  }, [map, position]);

  return null;
}

function LocationMarker({ onSelect, selectedPosition }) {
  const [position, setPosition] = useState(selectedPosition);

  useEffect(() => {
    setPosition(selectedPosition);
  }, [selectedPosition]);

  useMapEvents({
    async click(e) {
      const clickedPosition = e.latlng;
      setPosition(clickedPosition);

      let payload = {
        lat: clickedPosition.lat,
        lng: clickedPosition.lng,
        address: null,
        lookupStatus: "failed",
      };

      try {
        const fullAddress = await reverseGeocode(
          clickedPosition.lat,
          clickedPosition.lng,
        );

        payload = {
          lat: clickedPosition.lat,
          lng: clickedPosition.lng,
          address: fullAddress,
          lookupStatus: "ok",
        };
      } catch {
        payload = {
          lat: clickedPosition.lat,
          lng: clickedPosition.lng,
          address: null,
          lookupStatus: "failed",
        };
      }

      if (typeof onSelect === "function") {
        onSelect(payload);
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function SearchHandler({ searchQuery, onSelect }) {
  const map = useMap();

  useEffect(() => {
    if (!searchQuery) return;

    const doSearch = async () => {
      try {
        const results = await forwardGeocode(searchQuery);
        if (results && results.length > 0) {
          const result = results[0];
          const lat = parseFloat(result.lat);
          const lng = parseFloat(result.lon);
          map.setView([lat, lng], 15);

          if (typeof onSelect === "function") {
            onSelect({
              lat,
              lng,
              address: result,
              lookupStatus: "ok",
            });
          }
        }
      } catch (err) {
        console.error("Search failed:", err);
      }
    };

    doSearch();
  }, [searchQuery, map, onSelect]);

  return null;
}

export default function LocationPicker({
  onLocationSelect,
  locationValue = "",
  searchQuery = "",
}) {
  const selectedPosition = useMemo(
    () => parseLocationCoordinates(locationValue),
    [locationValue],
  );
  const mapCenter = selectedPosition
    ? [selectedPosition.lat, selectedPosition.lng]
    : DEFAULT_CENTER;

  return (
    <MapContainer center={mapCenter} zoom={13} style={{ height: "400px", width: "100%" }}>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="� OpenStreetMap"
      />
      <MapViewport position={selectedPosition} />
      <LocationMarker onSelect={onLocationSelect} selectedPosition={selectedPosition} />
      <SearchHandler searchQuery={searchQuery} onSelect={onLocationSelect} />
    </MapContainer>
  );
}
